// Feature-flag keys for every running experiment, in one place.
//
// The key here MUST match the flag key in PostHog exactly. A typo does not throw --
// posthog.getFeatureFlag returns undefined for an unknown key, which reads as "not in
// the experiment" and silently sends everyone down the control path. Add the key here
// and nowhere else, so there is one string to get right.
//
// PostHog: https://us.posthog.com/project/124207/experiments

// ---------------------------------------------------------------------------
// Surfaces
// ---------------------------------------------------------------------------
//
// Every experiment key is prefixed with the surface it runs on. This is the ONLY
// grouping baked into the key, and it is deliberate:
//
//   - The key is permanent. It is written into every $feature_flag_called event, so
//     once an experiment has run you cannot rename it without splitting its own
//     history. PostHog will not even let you change the key on a draft -- the update
//     is silently ignored -- so the prefix has to be right at creation.
//   - Surfaces outlive teams, quarters and initiatives. A key reading "q3-growth-..."
//     is noise a year later and cannot be fixed. Those dimensions belong on the flag's
//     TAGS, which are mutable and break no history.
//   - A prefix is what makes cross-experiment reporting possible: over
//     $feature_flag_called, `WHERE feature_flag LIKE 'checkout-%'` answers "what has
//     all our checkout experimentation done cumulatively, against the holdout?".
//     Tags live on the flag record and never reach the event stream, so they cannot.
export const SURFACE_CHECKOUT = "checkout";
export const SURFACE_INTAKE = "intake";
export const SURFACE_PORTAL = "portal";
export const SURFACE_LANDING = "landing";
export const SURFACE_EMR = "emr";
// The product / flavor pickers between intake and checkout (select-product,
// select-flavored, choose-flavor, try-flavored, select-ga-flavored,
// select-sleep-flavored): the questionnaire is done, the payment step not reached.
export const SURFACE_PRODUCT = "product";

// experimentKey builds a prefixed key. Keep the name short and distinctive --
// "checkout-one-time-disclosure", not "checkout-payment-step-disclosure-one-time-v2".
// Detail belongs in the experiment's description, not its key.
export const experimentKey = (surface, name) => `${surface}-${name}`;

// ---------------------------------------------------------------------------
// Live experiments
// ---------------------------------------------------------------------------
export const EXPERIMENT_ONE_TIME_PURCHASE_DISCLOSURE = experimentKey(
  SURFACE_CHECKOUT,
  "one-time-disclosure",
);
export const EXPERIMENT_CHECKOUT_DELIVERY_DATES = experimentKey(
  SURFACE_CHECKOUT,
  "delivery-dates",
);
export const EXPERIMENT_PRODUCT_FLAVOR_PHOTOS = experimentKey(
  SURFACE_PRODUCT,
  "flavor-photos",
);

// ---------------------------------------------------------------------------
// Variants
// ---------------------------------------------------------------------------
// PostHog's convention is that the baseline is "control"; anything the experiment
// introduces is "test".
export const VARIANT_CONTROL = "control";
export const VARIANT_TEST = "test";

// A user in the global holdout is excluded from every experiment and always sees the
// control experience. PostHog returns this as the variant value rather than "control",
// so treating it as anything other than control would split the baseline in two.
export const VARIANT_HOLDOUT_PREFIX = "holdout-";
